import { useState, useEffect, useId } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { X, FileText, Loader2, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Link, useLocation } from "@/lib/router-compat";
import { Turnstile } from "@/components/appointment/Turnstile";
import { cn } from "@/lib/utils";

const POPUP_DISMISSED_KEY = "maintenance-checklist-popup-dismissed";
const SITE_KEY = import.meta.env["VITE_TURNSTILE_SITE_KEY"] as
  string | undefined;

/** Pages where interrupting the visitor would cost more than the signup is worth. */
const SUPPRESSED_PATHS = [
  "/schedule",
  "/contact",
  "/maintenance-checklist",
  "/admin",
];

const formSchema = z.object({
  firstName: z.string().min(1, "First name is required").max(50),
  lastName: z.string().max(50).optional(),
  email: z.string().email("Please enter a valid email address"),
  phone: z.string().max(20).optional(),
  // Honeypot — must stay empty.
  website: z.string().max(0).optional(),
});

type FormData = z.infer<typeof formSchema>;

export const MaintenanceChecklistPopup = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [hasTriggered, setHasTriggered] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [done, setDone] = useState<{ emailed: boolean } | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [formStartTime, setFormStartTime] = useState(() => Date.now());
  const formId = useId();
  const { pathname } = useLocation();

  const suppressed = SUPPRESSED_PATHS.some(
    (p) => pathname === p || pathname.startsWith(`${p}/`),
  );

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormData>({ resolver: zodResolver(formSchema) });

  useEffect(() => {
    if (suppressed || hasTriggered) return;

    let dismissed = false;
    try {
      dismissed = sessionStorage.getItem(POPUP_DISMISSED_KEY) === "true";
    } catch {
      // Private mode or blocked storage — treat as not dismissed.
    }
    if (dismissed) {
      setHasTriggered(true);
      return;
    }

    const handleScroll = () => {
      const scrollable =
        document.documentElement.scrollHeight - window.innerHeight;
      if (scrollable <= 0) return;
      if (window.scrollY / scrollable >= 0.5) {
        setIsVisible(true);
        setHasTriggered(true);
        setFormStartTime(Date.now());
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [hasTriggered, suppressed]);

  const close = () => {
    setIsVisible(false);
    try {
      sessionStorage.setItem(POPUP_DISMISSED_KEY, "true");
    } catch {
      // Nothing to do; the popup just may reappear on the next page.
    }
  };

  const onSubmit = async (data: FormData) => {
    // Bot traps — behave as though it worked so a bot learns nothing.
    if (data.website && data.website.length > 0) {
      setDone({ emailed: true });
      return;
    }
    if (Date.now() - formStartTime < 2000) {
      setDone({ emailed: true });
      return;
    }

    setIsSubmitting(true);
    setServerError(null);
    try {
      const res = await fetch(
        `${import.meta.env["VITE_SUPABASE_URL"]}/functions/v1/checklist-signup`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${import.meta.env["VITE_SUPABASE_PUBLISHABLE_KEY"]}`,
          },
          body: JSON.stringify({
            firstName: data.firstName,
            lastName: data.lastName ?? "",
            email: data.email,
            phone: data.phone ?? "",
            turnstileToken: captchaToken,
          }),
        },
      );
      const payload = (await res.json().catch(() => ({}))) as {
        success?: boolean;
        emailed?: boolean;
        error?: string;
      };

      if (!res.ok || !payload.success) {
        setServerError(
          payload.error ||
            "We couldn't send that. Please try again in a moment.",
        );
        return;
      }

      reset();
      setDone({ emailed: payload.emailed !== false });
    } catch (err) {
      console.error("Checklist signup failed:", err);
      setServerError("Something went wrong. Please try again in a moment.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (suppressed || !isVisible) return null;

  return (
    <div className="fixed inset-0 z-100 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-xs animate-in fade-in duration-300"
        onClick={close}
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${formId}-title`}
        className={cn(
          "relative bg-card rounded-2xl shadow-2xl border border-border max-w-md w-full overflow-hidden max-h-[90vh] overflow-y-auto",
          "animate-in zoom-in-95 fade-in duration-300",
        )}
      >
        <button
          onClick={close}
          aria-label="Close"
          className="absolute top-4 right-4 p-1 rounded-full hover:bg-muted transition-colors z-10"
        >
          <X className="w-5 h-5 text-muted-foreground" aria-hidden="true" />
        </button>

        <div className="h-2 bg-gradient-to-r from-primary to-secondary" />

        <div className="p-6 md:p-8">
          {done ? (
            <div className="text-center">
              <CheckCircle2
                className="w-14 h-14 text-secondary mx-auto mb-5"
                aria-hidden="true"
              />
              <h3
                id={`${formId}-title`}
                className="text-2xl font-heading font-bold text-foreground mb-3"
              >
                {done.emailed ? "Check your inbox" : "Here's your checklist"}
              </h3>
              <p className="text-muted-foreground mb-6">
                {done.emailed
                  ? "We've emailed you the checklist. You can also read it right now."
                  : "We couldn't send the email just now, but you can read the checklist here."}
              </p>
              <Link to="/maintenance-checklist" onClick={close}>
                <Button
                  size="lg"
                  className="w-full bg-secondary hover:bg-secondary/90 text-secondary-foreground font-bold"
                >
                  Open the checklist
                </Button>
              </Link>
            </div>
          ) : (
            <>
              <div className="w-16 h-16 rounded-full bg-secondary/10 flex items-center justify-center mx-auto mb-5">
                <FileText
                  className="w-8 h-8 text-secondary"
                  aria-hidden="true"
                />
              </div>

              <div className="text-center mb-6">
                <h3
                  id={`${formId}-title`}
                  className="text-2xl font-heading font-bold text-foreground mb-3"
                >
                  Get your free home maintenance checklist
                </h3>
                <p className="text-muted-foreground text-sm">
                  A season-by-season guide to keeping a Minnesota roof, siding
                  and gutters in shape — including how to stop ice dams before
                  they start.
                </p>
              </div>

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <div className="absolute left-[-9999px]" aria-hidden="true">
                  <label htmlFor={`${formId}-website`}>
                    Website (leave blank)
                  </label>
                  <Input
                    id={`${formId}-website`}
                    tabIndex={-1}
                    autoComplete="off"
                    {...register("website")}
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor={`${formId}-firstName`} className="text-xs">
                      First name <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id={`${formId}-firstName`}
                      autoComplete="given-name"
                      className={errors.firstName ? "border-destructive" : ""}
                      {...register("firstName")}
                    />
                    {errors.firstName && (
                      <p className="text-xs text-destructive mt-1" role="alert">
                        {errors.firstName.message}
                      </p>
                    )}
                  </div>
                  <div>
                    <Label htmlFor={`${formId}-lastName`} className="text-xs">
                      Last name
                    </Label>
                    <Input
                      id={`${formId}-lastName`}
                      autoComplete="family-name"
                      {...register("lastName")}
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor={`${formId}-email`} className="text-xs">
                    Email <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id={`${formId}-email`}
                    type="email"
                    autoComplete="email"
                    className={errors.email ? "border-destructive" : ""}
                    {...register("email")}
                  />
                  {errors.email && (
                    <p className="text-xs text-destructive mt-1" role="alert">
                      {errors.email.message}
                    </p>
                  )}
                </div>

                <div>
                  <Label htmlFor={`${formId}-phone`} className="text-xs">
                    Phone{" "}
                    <span className="text-muted-foreground">(optional)</span>
                  </Label>
                  <Input
                    id={`${formId}-phone`}
                    type="tel"
                    autoComplete="tel"
                    {...register("phone")}
                  />
                </div>

                {SITE_KEY ? (
                  <Turnstile siteKey={SITE_KEY} onVerify={setCaptchaToken} />
                ) : null}

                {serverError && (
                  <p
                    className="text-sm text-destructive text-center"
                    role="alert"
                  >
                    {serverError}
                  </p>
                )}

                <Button
                  type="submit"
                  size="lg"
                  disabled={isSubmitting}
                  aria-busy={isSubmitting}
                  className="w-full bg-secondary hover:bg-secondary/90 text-secondary-foreground font-bold text-lg"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2
                        className="w-4 h-4 mr-2 animate-spin"
                        aria-hidden="true"
                      />
                      Sending...
                    </>
                  ) : (
                    "Send me the checklist"
                  )}
                </Button>

                <p className="text-[11px] text-muted-foreground text-center leading-relaxed">
                  We'll email you the checklist and the occasional seasonal
                  reminder from Cedar Creek Construction. Unsubscribe any time.
                </p>
              </form>
            </>
          )}

          <button
            onClick={close}
            className="w-full mt-4 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            {done ? "Close" : "No thanks, I'll skip this"}
          </button>
        </div>
      </div>
    </div>
  );
};
